import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Col, Row, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { injectIntl } from "react-intl";
import Moment from "moment";
import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { Helmet } from "react-helmet";
import { messages } from './Job.messages';
import { default as dashBoardMessages } from '../Dashboard/Dashboard.messages';
import useI18n from "../../hooks/useI18n";

// services
import { userLogin, userLoggedin, userGetToken, userRequestHeaders } from '../../services/user/index';
import { jobGetCode, jobGetJob, jobFetchJob, jobGetGovidJob } from '../../services/job/index';
import { paymentService } from '../../services/payment/index';
import { toggleLoading, changeLoadingText } from "../../services/helpers/index";

// cards
import ServingCard from '../../components/ServingCard.js';

// components
import AlertDialog from '../../components/AlertDialog.js';
import CompanyCard from "../../components/CompanyCard";
import CompanyBanner from '../../components/CompanyBanner.js';
import useWhiteLabeling from "../../hooks/useWhiteLabeling";
import InvoiceSummaryCard from '../../components/InvoiceSummaryCard.js';
import InvoiceDetails from '../../components/InvoiceDetails.js';
import SubJobs from '../../components/SubJobs.js';
import CompanyInfoModal from "../../components/CompanyInfoModal";
import useTime from "../../hooks/useTime";
import JobAdditionalActions from "../../components/JobAdditionalActions";
import JobSupport from "../../components/JobSupport";
import useSettings from "../../hooks/useSettings";
import useMessages from "../../hooks/useMessages";
import JobMessages from "../../components/JobMessages";
import useUserAccount from "../../hooks/useUserAccount";
import useSessionStorage from "../../hooks/useSessionStorage";

const LOGO_MAX_HEIGHT = process.env.REACT_APP_WHITELABEL_LOGO_MAX_HEIGHT !== undefined && process.env.REACT_APP_WHITELABEL_LOGO_MAX_HEIGHT !== ''
	? parseInt(process.env.REACT_APP_WHITELABEL_LOGO_MAX_HEIGHT, 10)
	: 56;

const Job = (props) => {
	const lng = props.intl.formatMessage;
	const { userInitialize, userIsAuthenticated } = useUserAccount();
	const isAuthed = userIsAuthenticated() && jobGetCode() !== null;
	const [showAlert, setShowAlert] = useState(false);
	const [showAlertMessage, setShowAlertMessage] = useState('');
	const [showAlertType, setShowAlertType] = useState(undefined);
	const [isAuthenticating, setIsAuthenticating] = useState(true);
	const [job, setJob] = useState(null);
	const [disableServingButton, setDisableServingButton] = useState(false);
	const [showServingModal, setShowServingModal] = useState(false);
	const [currency, setCurrency] = useState(process.env.REACT_APP_DEFAULT_CURRENCY);
	const [showCompanyInfoModal, setShowCompanyInfoModal] = useState(false);
	// toggle job details in mobile view
	const [currentIndex, setCurrentIndex] = useState(-1);
	const [currentPage, setCurrentPage] = useState(-1);
	const [lastPage, setLastPage] = useState(-1);
	const [showBrowseButton, setShowBrowseButton] = useState(false);
	const [firstInGovidJobs, setFirstInGovidJobs] = useState(false);
	const [lastInGovidJobs, setLastInGovidJobs] = useState(false);
	const { messageCenterMessages } = useMessages();
	// Wizard
	const [showWizardButton, setShowWizardButton] = useState(false);
	const [isDirectLinkOverpaid, setIsDirectLinkOverpaid] = useState(false);
	const [overpaidInformation, setOverpaidInformation] = useState([]);
	const [showOverpaidInformation, setShowOverpaidInformation] = useState(false);
	const [showOverpaidWizard, setShowOverpaidWizard] = useState(false);

	// Chat
	const [livechatParameters, setLivechatParameters] = useState([]);

	// Take alert-content from location-prop
	const { message, type } = (props.location?.alert) || {};

	// Message Center
	const [showNewMessageButton, setShowNewMessageButton] = useState(true);
	const [allMessages, setAllMessages] = useState([]);

	// White label
	const { fetchWhiteLabelingData } = useWhiteLabeling();
	const [whiteLabelData, setWhiteLabelData] = useState(null);
	const [buttonColor, setButtonColor] = useState(null);
	const [buttonTextColor, setButtonTextColor] = useState(null);
	const [logoWidth, setLogoWidth] = useState(null);
	const [logoHeight, setLogoHeight] = useState(null);

	const { addDaysToDate } = useTime();
	const { getLocale } = useI18n();
	const {
		allowServing,
		showChatButton,
		showMessageCenterButton
	} = useSettings();

	const userLocale = getLocale();

	// Support section should be shown, if either the chat or message center is available
	const showSupportSection = (showChatButton || showMessageCenterButton);

	// check if user is authorized to use the message center
	const messageCenterAuthorized = (showMessageCenterButton && isAuthed);

	const paymentMethod = props.match?.params?.paymentmethod;

	const { setjobTokenUuid } = useSessionStorage();

	// check if environment allows serving and if job is suitable for it
	const showServing = (allowServing && job?.hasserving);

	useEffect(() => {
		// Fetch initial messages when component mounts
		const resolveMessages = () => {
			const jobMessages = messageCenterMessages.filter(msg => msg.jobid === job.jobid.toString());
			setAllMessages(jobMessages);
			if (jobMessages?.length > 0) {
				const isClosed = jobMessages.every(message => (
					message.current_status?.status === 'Closed'
				));
				setShowNewMessageButton(isClosed);
			} else {
				setShowNewMessageButton(true);
			}
		}
		if (!props.maintenance && messageCenterAuthorized && job?.jobid) {
			resolveMessages()
		} else {
			setAllMessages([]);
		}
	}, [props.maintenance, messageCenterMessages, messageCenterAuthorized, job?.jobid]);

	useEffect(() => {
		// Loading animation
		toggleLoading(true, lng(messages.loading) + '...');

		// Show alerts that are being given by previous view
		if (message !== undefined) {
			handleAlertDialog(message, type);
		}

		if (props.match.params.uuid) {
			userLogin(props.match.params.uuid)
				.then(token => {
					userInitialize(token);
					changeLoadingText(lng(messages.searchingForInvoice));
					jobFetchJob()
						.then(function (_response) {
							const job = jobGetJob();

							// Sets a possible new paydate already
							job.newpaydate = addDaysToDate(job.paydate, 14);
							job.firstpaydate = Moment(job.paydate).format('YYYY-MM-DD');

							if (Moment(job.paydate).format('x') < Moment().format('x')) {
								job.firstpaydate = Moment().format('YYYY-MM-DD');
							}

							const jobCurrency = get(job, 'currency', false);

							// Set currency if set on the job data
							if (jobCurrency && jobCurrency !== '') {
								setCurrency(jobCurrency);
							}

							// sets job's details
							setJob(job);

							// Show wizard if job status is 302 or 104 and account number is not yet given
							if (job.statuscode === 302 || job.statuscode === 104) {
								// Check if bank account number has already been notified
								if (job.modoverpaid !== null && job.modoverpaid) {
									// If IBAN has not been notified, open wizard invoice data step
                                    setShowOverpaidWizard(true);
									setShowWizardButton(true);
								} else {
                                    setShowOverpaidWizard(false);
									setShowWizardButton(false);
								}
								setIsDirectLinkOverpaid(true);
							}

							// Forced payment service exists?
							const supportedServices = Object.values(paymentService.PAYMENTMETHODS);

							if (supportedServices.includes(paymentMethod)) {
								window.sessionStorage.setItem('paymentService', paymentMethod);
							} else if (window.sessionStorage.getItem('paymentService') !== null) {
								// If there is no payment service in the url, but session storage
								// value exists, remove values from the session storage
								window.sessionStorage.removeItem('paymentService');
							}
						})
						.catch(function (error) {
							toggleLoading(false);
							if (error.response?.status === 401) {
								// Authentication needed but without error, redirect back to authentication
								sessionStorage.clear();
								const url = props.location.pathname;
								const jobTokenUuid = url.replace("/job/", "");
								setjobTokenUuid(jobTokenUuid);
								props.history.push({
									pathname: '/',
								});
							} else {
								// Failed request
								const alert = { message: lng(messages.somethingWentWrong), type: 'danger' };
								props.history.push({
									pathname: "/",
									alert
								});
							}

						});
				}, (error) => {
					toggleLoading(false);
					if (error.response?.status === 401) {
						const alert = { message: lng(messages.authError), type: 'danger' };
						props.history.push({
							pathname: "/logout",
							alert
						});
					} else {
						// Failed request
						const alert = { message: lng(messages.authError), type: 'danger' };
						props.history.push({
							pathname: "/",
							alert
						});
					}
				});
		} else if (userLoggedin() === false) {
			const alert = { message: lng(messages.authRequired), type: 'primary' };
			props.history.push({
				pathname: "/",
				alert
			});

		} else if (jobGetJob() === null) {
			const alert = { message: lng(messages.retrySearch), type: 'primary' };
			props.history.push({
				pathname: "/jobsearch",
				alert
			});

		} else {
			const job = jobGetJob();

			// Sets a possible new paydate already
			job.newpaydate = addDaysToDate(job.paydate, 14);
			job.firstpaydate = Moment(job.paydate).format('YYYY-MM-DD');

			if (Moment(job.paydate).format('x') < Moment().format('x')) {
				job.firstpaydate = Moment().format('YYYY-MM-DD');
			}

			const jobCurrency = get(job, 'currency', false);

			// Set currency if set on the job data
			if (jobCurrency && jobCurrency !== '') {
				setCurrency(jobCurrency);
			}

			setJob(job);
		}

		window.scrollTo(0, 0);

	}, []);

	// When job is updated, perform other related actions
	useEffect(() => {
		if (!isEmpty(job)) {
			// Fetch white label data
			fetchWhiteLabelingData(userLocale, job.cid, job.jobid).then(res => {
				if (res) {
					setWhiteLabelData(res);
					setBrandingSettings(res);
				} else {
					setWhiteLabelData(null);
					setBrandingSettings(null);
				}
			});

			if (!isAuthed && showServing && job.serving_data?.data?.serving_need_signature) {
				// serving cannot be signed, if user has not logged in
				handleAlertDialog(lng(messages.documentToSign) + ". " + lng(messages.strongAuthenticationNeeded), 'danger');
				setDisableServingButton(true);
			}

			// sets parameters for LiveChat
			setLivechatParameters([
				{ name: "jobid", value: job.jobid },
				{ name: "token", value: userGetToken() }
			])

			// check if browse button between jobs should be shown
			if (jobGetGovidJob() !== null) {
				browseButtonsDetails();
			}

			if (props.maintenance) {
				handleAlertDialog(lng(messages.underMaintenance), 'warning');
			}

			toggleLoading(false);
			// allows to view to be rendered
			setIsAuthenticating(false);
		}

	}, [job, props.maintenance]);

	/**
	 * Set white labeling data to the state.
	 * @param {object|null} data
	 */
	function setBrandingSettings(data) {
		if (data?.brandingSettings) {
			setButtonColor(data.brandingSettings.button_color);
			setButtonTextColor(data.brandingSettings.button_text_color);
			if (data.brandingSettings.logo_width && data.brandingSettings.logo_height) {
				const aspectRatio = data.brandingSettings.logo_width / data.brandingSettings.logo_height;
				const height = LOGO_MAX_HEIGHT > 0
					? Math.min(data.brandingSettings.logo_height, LOGO_MAX_HEIGHT)
					: data.brandingSettings.logo_height;
				setLogoWidth(height * aspectRatio);
				setLogoHeight(height);
			}
		} else {
			setButtonColor(null);
			setButtonTextColor(null);
			setLogoWidth(null);
			setLogoHeight(null);
		}
	}

	/**
	 * Add details to browse buttons: current page and number of pages altogether.
	 * Also keep track of the index of the current job.
	 */
	function browseButtonsDetails() {
		setFirstInGovidJobs(false);
		setLastInGovidJobs(false);
		const govidJobs = jobGetGovidJob();
		let currenctIndex = 0;

		// check the index of currently open job, returns -1 if no matching dpid is found
		currenctIndex = govidJobs.findIndex((govidJob) => govidJob.dpid === job.dpid);

		// browse button should not be visible if job is not in the govid job listing
		if (currenctIndex > -1) {
			// set page numbers and index and save current index
			setCurrentPage(currenctIndex + 1);
			setLastPage(govidJobs.length);
			setCurrentIndex(currenctIndex);

			// check if job is first or last (affets e.g. button styling)
			if (currenctIndex === 0) {
				setFirstInGovidJobs(true);
			}
			if (currenctIndex === govidJobs.length - 1) {
				setLastInGovidJobs(true);
			}

			// show button for browsing
			setShowBrowseButton(true);
		}
	}

	const handleOverpaidInfo = (wizardOverpaid) => {
		if (wizardOverpaid.show) {
			setOverpaidInformation(wizardOverpaid);
			setShowOverpaidInformation(true);
		}
	}

	/**
	 * Get the previous job from govidjob list.
	 */
	function getPreviousJob() {
		if (currentIndex > 0) {
			const prevDpid = currentIndex - 1;
			const previousJob = jobGetGovidJob()[prevDpid].dpid;

			handleBrowseJob(previousJob);
		}
	}

	/**
	 * Get the next job from govidjob list.
	 */
	function getNextJob() {
		const govidJobs = jobGetGovidJob();

		if (currentIndex < govidJobs.length - 1) {
			const nextDpid = currentIndex + 1;
			const nextJob = govidJobs[nextDpid].dpid;

			handleBrowseJob(nextJob);
		}
	}

	/**
	 * Browse jobs by fetching the previous or next job from listed govid jobs with dpid.
	 * @param {*} dpid
	 */
	function handleBrowseJob(dpid) {
		toggleLoading(true, lng(messages.searchingForInvoice));
		setShowAlert(false); // hide any remaining error messages
		setDisableServingButton(false); // ensure serving button state is also reset 

		// fetch previous or next job based on the dpid
		jobFetchJob(dpid).then(function () {
			toggleLoading(false);
			const newJob = jobGetJob();

			// Sets a possible new paydate already
			newJob.newpaydate = addDaysToDate(newJob.paydate, 14);
			newJob.firstpaydate = Moment(newJob.paydate).format('YYYY-MM-DD');

			if (Moment(newJob.paydate).format('x') < Moment().format('x')) {
				newJob.firstpaydate = Moment().format('YYYY-MM-DD');
			}

			const jobCurrency = get(newJob, 'currency', false);

			// Set currency if set on the job data
			if (jobCurrency && jobCurrency !== '') {
				setCurrency(jobCurrency);
			}

			setJob(newJob);

		}).catch(function (error) {
			toggleLoading(false);
			if (typeof error?.response !== 'undefined') {
				if (error.response.status === 401) {
					// authentication failed, redirect back to authentication
					const alert = { message: lng(messages.authError), type: 'danger' };
					props.history.push({
						pathname: "/logout",
						alert
					});
				}
			} else {
				// request failed completely
				handleAlertDialog(lng(messages.somethingWentWrongTryAgain), 'danger');
			}
		});
	}

	/**
	 * Handle alertDialog. Function takes message and type as parameters
	 * and show the alertdialog above all the details related to the invoice.
	 *
	 * @param {string} message
	 * @param {string} type
	 */
	function handleAlertDialog(message, type) {
		setShowAlertMessage(message);
		setShowAlertType(type);
		setShowAlert(true);
	}

	/**
	 * Show serving modal. 
	 * 
	 * Serving modal can be opened from multiple places,
	 * so handle visibility in parent.
	 */
	const handleShowServingModal = () => {
		if (!disableServingButton) {
			setShowServingModal(true);
		}
	};

	/**
	 * Hide serving modal.
	 */
	const handleHideServingModal = () => {
		if (!disableServingButton) {
			setShowServingModal(false);
		}
	};

	const handleCompanyCardClick = () => {
		setShowCompanyInfoModal(true);
	}

	const handleHideCompanyInfoModal = () => {
		setShowCompanyInfoModal(false);
	}

	if (isAuthenticating) {
		return <></>
	}

	// job page (jobs other than overpayment (302) or credit note (104))
	return (
		<div id='job' className='content flex-fill'>
			<Helmet id='job-helmet'>
				<title>{lng(messages.jobid)} {job.jobid.toString()} | MyRopo</title>
			</Helmet>
			<Container id='job-page' className={'mb-2 ' + (isAuthed ? '' : 'mt-4')}>
				{isAuthed && (
					<Row className='align-items-center'>
						<Col xs={10} >
							<Link className='btn to-jobsearch-btn text-start ps-2 d-none d-xl-block' to='/jobsearch' id='to-jobsearch' aria-labelledby='to-jobsearch-btn-heading'>
								<h5 id="to-jobsearch-btn-heading">
									<FontAwesomeIcon icon={far.faArrowLeft} /> &nbsp;
									{lng(messages.goBack)}
								</h5>
							</Link>
						</Col>
						{showBrowseButton && (
							<Col xs={{ span: 1, offset: 6 }} xl={{ span: 2, offset: 0 }} className='d-flex justify-content-center justify-content-xl-end'>
								<div className={'d-flex gap-0 browse' + (firstInGovidJobs && lastInGovidJobs ? ' browse-disabled' : '')}>
									<Button className={'btn browse-job previous' + (firstInGovidJobs ? ' browse-disabled' : '')} onClick={() => getPreviousJob()} variant='browse' title={!firstInGovidJobs ? lng(messages.moveToPreviousJob) : ''} aria-label={lng(messages.moveToPreviousJob)}> <FontAwesomeIcon icon={far.faChevronLeft} className='me-2' aria-hidden="true" /> {currentPage}</Button>
									<span>/</span>
									<Button className={'btn browse-job next' + (lastInGovidJobs ? ' browse-disabled' : '')} onClick={() => getNextJob()} variant='browse' title={!lastInGovidJobs ? lng(messages.moveToNextJob) : ''} aria-label={lng(messages.moveToNextJob)}> {lastPage} <FontAwesomeIcon icon={far.faChevronRight} className='ms-2' aria-hidden="true" /></Button>
								</div>
							</Col>
						)}
					</Row>
				)}
				<Row className={'pb-4 m-0 m-sm-1 '}>
					{/*Overpaid information*/}
					{showOverpaidInformation && (
						<AlertDialog
							message={<div>{lng(dashBoardMessages.overpaidInformation1)} <b>{overpaidInformation.account}</b> {lng(dashBoardMessages.overpaidInformation2)} <b>{overpaidInformation.name}</b>. {lng(dashBoardMessages.returnAmount)}: <b>{overpaidInformation.amount}</b>. {lng(dashBoardMessages.refundProcessingFee)}: <b>{overpaidInformation.fee}</b>.</div>}
							type='success'
						/>
					)}
				</Row>
				{/* Alerts and successes to show on the invoice page */}
				<Row className='ps-3 pe-3 '>
					{showAlert &&
						<AlertDialog
							message={showAlertMessage}
							type={showAlertType}
						/>
					}
				</Row>
				<Row>
					{/* show info for jobs other than overpayments (302) or credit notes (104) */}
					{job !== null && job.statuscode !== 302 && job.statuscode !== 104 && (
						<Col xs={{ span: 12, order: 1 }} md={{ span: 8, order: 1 }} className='p-0 ps-sm-3 pe-sm-3' >
							<main
								id="main"
								aria-label={job.payee.name + ' ' + lng(messages.invoiceOverview)}
							>
								<CompanyCard
									companyname={job.payee.name}
									data={whiteLabelData}
									logoHeight={logoHeight}
									logoWidth={logoWidth}
									onClick={handleCompanyCardClick}
								/>
								<InvoiceSummaryCard
									job={job}
									toggleLoading={toggleLoading}
									buttonColor={buttonColor}
									buttonTextColor={buttonTextColor}
									currency={currency}
									isAuthed={isAuthed}
									handleAlertDialog={handleAlertDialog}
									paymentMethod={paymentMethod}
									onUpdateJob={setJob}
								/>

								{whiteLabelData && whiteLabelData.banners?.length > 0 && (
									<span className='d-md-none'>
										<CompanyBanner banner={whiteLabelData.banners[0]} />
									</span>
								)}
								<InvoiceDetails
									job={job}
									{...props}
								/>

								{job.members !== null && (
									<SubJobs
										job={job}
										buttonColor={buttonColor}
										buttonTextColor={buttonTextColor}
										{...props}
									/>
								)}
							</main>
						</Col>
					)}
					<Col xs={{ span: 12, order: 2 }} md={{ span: isDirectLinkOverpaid ? 8 : 4, order: 2 }} className={isDirectLinkOverpaid ? '' : 'p-0 ps-sm-3 pe-sm-3 ps-md-0'}>
						{/* show for all jobs */}
						<aside
							aria-label={showSupportSection ? lng(messages.actions) + ' & ' + lng(messages.getSupport) : lng(messages.actions)}
						>
							{job !== null && (
								<>
									{/* Serving info shown in side column, if serving is allowed in the environment and job has serving data*/}
									{showServing && (
										<ServingCard
											job={job}
											servingData={job.serving_data.data}
											toggleLoading={toggleLoading}
											onUpdateJob={setJob}
											handleAlertDialog={handleAlertDialog}
											onDisableServingButton={setDisableServingButton}
											disableServingButton={disableServingButton}
											showServingModal={showServingModal}
											onShowServingModal={handleShowServingModal}
											onHideServingModal={handleHideServingModal}
											isAuthed={isAuthed}
											{...props}
										/>
									)}
									{whiteLabelData && whiteLabelData.banners?.length > 0 && (
										<span className='d-none d-md-block'>
											<CompanyBanner banner={whiteLabelData.banners[0]} />
										</span>
									)}
									<JobAdditionalActions
										handleAlertDialog={handleAlertDialog}
										toggleLoading={toggleLoading}
										job={job}
										servingButtonDisabled={disableServingButton}
										onShowServingModal={handleShowServingModal}
										showWizardButton={showWizardButton && !overpaidInformation.show}
										showServing={showServing && job.serving_data?.data?.serving_need_signature}
										isDirectLinkOverpaid={isDirectLinkOverpaid}
										showOverpaidWizard={showOverpaidWizard}
										setShowOverpaidWizard={setShowOverpaidWizard}
										onOverpaidInfo={handleOverpaidInfo}
									/>
								</>
							)}
							{showSupportSection && job !== null && (
								<JobSupport
									showMessageCenterButton={showMessageCenterButton}
									showChatButton={showChatButton}
									showNewMessageButton={showNewMessageButton}
									livechatParameters={livechatParameters}
									messageCenterAuthorized={messageCenterAuthorized}
									maintenance={props.maintenance}
									toggleLoading={toggleLoading}
									job={job}
								/>
							)}
						</aside>
						{allMessages.length > 0 && (
							<aside
								aria-label={lng(messages.messagesRelatedToJob)}
								aria-live='polite'
							>
								<JobMessages
									messages={allMessages}
									title={lng(messages.messageCenterMessages)}
								/>
							</aside>
						)}
					</Col>
				</Row>
			</Container>
			<CompanyInfoModal
				companyName={job.payee?.name}
				onHide={handleHideCompanyInfoModal}
				show={showCompanyInfoModal}
				data={whiteLabelData}
				logoHeight={logoHeight}
				logoWidth={logoWidth}
			/>
		</div>
	);
};

Job.propTypes = {
	'intl': PropTypes.object.isRequired,
	'match': PropTypes.object,
	'match.params': PropTypes.object,
	'match.params.paymentmethod': PropTypes.string,
	'location': PropTypes.object,
	'location.alert': PropTypes.object,
	'history': PropTypes.any,
	'maintenance': PropTypes.bool.isRequired
};

export default injectIntl(Job);
